import clsx from 'clsx';
import { useTranslation } from 'next-i18next/pages';
import { ButtonHTMLAttributes, MouseEventHandler } from 'react';

import { ReactComponent as ArrowBack } from '@pflegia/ui/icons/arrow-back.svg';
import { isIosSafari, isMobile } from '@pflegia/util/functions/device-detection';

export interface MultipostingButtonsProps {
  nextButtonType?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
  onBack?: MouseEventHandler<HTMLButtonElement>;
  onNext?: MouseEventHandler<HTMLButtonElement>;
  onSkip?: MouseEventHandler<HTMLButtonElement>;
  skipLabel?: string;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
}

const MultipostingButtons: React.FC<MultipostingButtonsProps> = ({
  nextButtonType = 'button',
  onBack,
  onNext,
  onSkip,
  skipLabel,
  disabled,
  loading,
  className,
}) => {
  const { t } = useTranslation('common');
  const extraClasses = [disabled && 'grayscale', loading && 'animate-pulse'];

  return (
    <div
      className={clsx(
        'fixed bottom-0 left-0 grid w-full overflow-hidden font-medium md:static md:gap-4 md:overflow-auto',
        isIosSafari() && isMobile() && 'border-b border-white',
        onSkip ? 'md:grid-cols-[repeat(3,minmax(150px,max-content))]' : 'md:grid-cols-[repeat(2,minmax(150px,max-content))]',
        className,
      )}>
      {onSkip && (
        <button
          className={clsx(BUTTON_CLASSES, 'border-primary text-primary border bg-white', extraClasses)}
          onClick={disabled || loading ? undefined : onSkip}
          type='button'>
          {skipLabel || t('skip')}
        </button>
      )}
      {(onNext || nextButtonType === 'submit') && (
        <button
          className={clsx(BUTTON_CLASSES, 'bg-primary text-white', extraClasses)}
          disabled={loading}
          onClick={onNext}
          type={nextButtonType}>
          {t('next')}
        </button>
      )}
      {onBack && (
        <button
          className={clsx(
            BUTTON_CLASSES,
            'text-default flex items-center justify-center gap-2 bg-white md:col-start-1 md:row-start-1',
            extraClasses,
          )}
          onClick={disabled || loading ? undefined : onBack}
          type='button'>
          <ArrowBack className='hidden md:block' />
          {t('back')}
        </button>
      )}
    </div>
  );
};

const BUTTON_CLASSES = 'p-3 transition-shadow hover:shadow-lg disabled:grayscale hover:disabled:shadow-none md:rounded-full';

export default MultipostingButtons;
